import React, { ReactElement, useMemo, useState } from "react";
import { Col, Container, Row, Table, Form } from "react-bootstrap";
import DatePicker from "react-datepicker";
import Chart from "react-apexcharts";
import { StatsService } from "../stats";
import { UserService, IUserTableData } from "../users";
import { Loading } from "../ui";

interface SalesCycleSummary {
	avgWon: number;
	avgLost: number;
	avgAll: number;
	totalWon: number;
	totalLost: number;
}

interface Distribution {
	[key: string]: number;
}

interface RepData {
	name: string;
	avgWon: number;
	avgLost: number;
	totalDeals: number;
}

interface ProductData {
	product: string;
	avgDays: number;
	totalDeals: number;
}

interface OutlierData {
	id: number;
	name: string;
	account: string;
	rep: string;
	days: number;
	amount: number;
	stage: string;
}

interface SalesCycleData {
	summary: SalesCycleSummary;
	distribution: Distribution;
	byRep: RepData[];
	byProduct: ProductData[];
	outliers: OutlierData[];
}

export const SalesCyclePage = (): ReactElement => {
	const [date, setDate] = useState<Date | null>(new Date());
	const [userId, setUserId] = useState<number | undefined>(undefined);
	const [users, setUsers] = useState<IUserTableData[]>([]);
	const [data, setData] = useState<SalesCycleData | null>(null);
	const [loading, setLoading] = useState(true);

	const statsService = new StatsService();
	const userService = new UserService();

	const fetchUsers = async () => {
		const userList = await userService.getUsersForTable(true);
		setUsers(userList.filter(u => u.include_in_dashboard_stats));
	};

	const fetchData = async () => {
		if (!date) return;
		setLoading(true);
		const result = await statsService.findSalesCycle(date, userId);
		setData(result as SalesCycleData);
		setLoading(false);
	};

	useMemo(() => {
		fetchUsers();
	}, []);

	useMemo(() => {
		fetchData();
	}, [date, userId]);

	const formatCurrency = (value: number) => {
		return new Intl.NumberFormat("en-US", {
			style: "currency",
			currency: "USD",
			minimumFractionDigits: 0,
			maximumFractionDigits: 0,
		}).format(value);
	};

	return (
		<Container fluid className="dashboard-container">
			<div className="dashboard-header-card">
				<div className="dashboard-header-main">
					<div className="dashboard-icon-wrapper">
						<i className="fa fa-clock-o"></i>
					</div>
					<div className="dashboard-info">
						<h1 className="dashboard-title">Sales Cycle Length</h1>
						<p className="dashboard-subtitle">
							Average days from opportunity creation to close
						</p>
					</div>
				</div>
				<div className="dashboard-header-actions">
					<Form.Select
						className="me-2"
						style={{ width: "200px", display: "inline-block" }}
						value={userId || ""}
						onChange={(e) => setUserId(e.target.value ? Number(e.target.value) : undefined)}
					>
						<option value="">All Sales Reps</option>
						{users.map((user) => (
							<option key={user.id} value={user.id}>
								{user.full_name}
							</option>
						))}
					</Form.Select>
					<div className="date-picker-wrapper">
						<i className="fa fa-calendar"></i>
						<DatePicker
							className="form-control date-picker-input"
							dateFormat="MM/yyyy"
							selected={date}
							showMonthYearPicker
							onChange={(_date: Date | null) => setDate(_date)}
						/>
					</div>
				</div>
			</div>

			<div className="dashboard-card mb-4">
				<div className="card-header">
					<i className="fa fa-info-circle"></i>
					<span>About This Report</span>
				</div>
				<div className="card-body">
					<p><strong>What this report shows:</strong> Analyzes how long it takes to close deals, helping you understand your sales velocity and identify deals that may need attention.</p>
					<p><strong>How data is calculated:</strong></p>
					<ul>
						<li><strong>Avg Days (Won):</strong> The average number of days between opportunity creation and close date for deals that were closed-won during the selected month.</li>
						<li><strong>Avg Days (Lost):</strong> The average number of days for deals that were closed-lost. Typically longer than won deals, as lost deals often linger before being marked as lost.</li>
						<li><strong>Distribution Chart:</strong> Groups all closed deals into time buckets (0-7 days, 8-14 days, etc.) to show the spread of your sales cycle.</li>
						<li><strong>By Sales Rep:</strong> Compares average cycle length across team members to identify efficiency differences.</li>
						<li><strong>By Product Line:</strong> Shows which products close faster or slower, useful for setting realistic expectations.</li>
						<li><strong>Outliers (90+ Days):</strong> Flags deals that took unusually long to close, which may indicate complex sales or deals that should have been disqualified earlier.</li>
					</ul>
					<p><strong>How to use:</strong> Use this report to set realistic sales forecasts and identify process improvements. If lost deals take much longer than won deals, consider implementing earlier disqualification criteria. Compare reps to share best practices from faster closers. Review outliers to understand what causes delays and whether those deals were worth pursuing.</p>
				</div>
			</div>

			{loading ? (
				<div className="text-center p-5">
					<Loading /> Loading sales cycle data...
				</div>
			) : data ? (
				<>
					<Row className="mb-4">
						<Col lg={4}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-trophy"></i>
									<span>Avg Days (Won)</span>
								</div>
								<div className="card-body text-center">
									<h2 className="text-success" style={{ fontSize: "2.5rem" }}>
										{data.summary.avgWon}
									</h2>
									<small className="text-muted">
										{data.summary.totalWon} deals closed
									</small>
								</div>
							</div>
						</Col>
						<Col lg={4}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-times-circle"></i>
									<span>Avg Days (Lost)</span>
								</div>
								<div className="card-body text-center">
									<h2 className="text-danger" style={{ fontSize: "2.5rem" }}>
										{data.summary.avgLost}
									</h2>
									<small className="text-muted">
										{data.summary.totalLost} deals lost
									</small>
								</div>
							</div>
						</Col>
						<Col lg={4}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-calendar"></i>
									<span>Total Deals</span>
								</div>
								<div className="card-body text-center">
									<h2 className="text-primary" style={{ fontSize: "2.5rem" }}>
										{data.summary.totalWon + data.summary.totalLost}
									</h2>
									<small className="text-muted">
										Avg: {data.summary.avgAll} days
									</small>
								</div>
							</div>
						</Col>
					</Row>

					<Row className="mb-4">
						<Col lg={12}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-bar-chart"></i>
									<span>Sales Cycle Distribution</span>
								</div>
								<div className="card-body">
									<Chart
										options={{
											chart: {
												type: "bar",
											},
											plotOptions: {
												bar: {
													borderRadius: 4,
													horizontal: false,
													columnWidth: "60%",
												},
											},
											xaxis: {
												categories: Object.keys(data.distribution),
											},
											colors: ["#4361ee"],
											dataLabels: {
												enabled: true,
											},
											yaxis: {
												title: {
													text: "Number of Deals",
												},
											},
										}}
										series={[
											{
												name: "Deals",
												data: Object.values(data.distribution),
											},
										]}
										type="bar"
										height={300}
									/>
								</div>
							</div>
						</Col>
					</Row>

					<Row className="mb-4">
						<Col lg={6}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-users"></i>
									<span>By Sales Rep</span>
								</div>
								<div className="card-body">
									<Table striped hover responsive>
										<thead>
											<tr>
												<th>Sales Rep</th>
												<th className="text-end">Avg Won</th>
												<th className="text-end">Avg Lost</th>
												<th className="text-end">Total Deals</th>
											</tr>
										</thead>
										<tbody>
											{data.byRep.map((rep) => (
												<tr key={rep.name}>
													<td>{rep.name}</td>
													<td className="text-end text-success">{rep.avgWon} days</td>
													<td className="text-end text-danger">{rep.avgLost} days</td>
													<td className="text-end">{rep.totalDeals}</td>
												</tr>
											))}
										</tbody>
									</Table>
									{data.byRep.length === 0 && (
										<div className="text-center text-muted p-3">
											No data available
										</div>
									)}
								</div>
							</div>
						</Col>
						<Col lg={6}>
							<div className="dashboard-card">
								<div className="card-header">
									<i className="fa fa-cube"></i>
									<span>By Product Line</span>
								</div>
								<div className="card-body">
									<Table striped hover responsive>
										<thead>
											<tr>
												<th>Product Line</th>
												<th className="text-end">Avg Days</th>
												<th className="text-end">Total Deals</th>
											</tr>
										</thead>
										<tbody>
											{data.byProduct.map((product) => (
												<tr key={product.product}>
													<td>{product.product}</td>
													<td className="text-end">{product.avgDays} days</td>
													<td className="text-end">{product.totalDeals}</td>
												</tr>
											))}
										</tbody>
									</Table>
									{data.byProduct.length === 0 && (
										<div className="text-center text-muted p-3">
											No data available
										</div>
									)}
								</div>
							</div>
						</Col>
					</Row>

					{data.outliers.length > 0 && (
						<Row>
							<Col lg={12}>
								<div className="dashboard-card">
									<div className="card-header">
										<i className="fa fa-warning"></i>
										<span>Outliers (90+ Days)</span>
									</div>
									<div className="card-body">
										<Table striped hover responsive>
											<thead>
												<tr>
													<th>Opportunity</th>
													<th>Account</th>
													<th>Sales Rep</th>
													<th className="text-end">Days</th>
													<th className="text-end">Amount</th>
													<th>Stage</th>
												</tr>
											</thead>
											<tbody>
												{data.outliers.map((opp) => (
													<tr key={opp.id}>
														<td>
															<a
																href={`/opportunities/${opp.id}`}
																target="_blank"
																rel="noopener noreferrer"
															>
																{opp.name}
															</a>
														</td>
														<td>{opp.account}</td>
														<td>{opp.rep}</td>
														<td className="text-end text-warning">
															<strong>{opp.days}</strong>
														</td>
														<td className="text-end">{formatCurrency(opp.amount)}</td>
														<td>
															<span
																className={
																	opp.stage.includes("Won")
																		? "text-success"
																		: "text-danger"
																}
															>
																{opp.stage}
															</span>
														</td>
													</tr>
												))}
											</tbody>
										</Table>
									</div>
								</div>
							</Col>
						</Row>
					)}
				</>
			) : (
				<div className="text-center p-5">No data available</div>
			)}
		</Container>
	);
};
